
/**
 * Linked PDP Product Swap
 * Shopify Impulse + kit-variant-picker
 *
 * Cambia entre productos vinculados sin recargar la página.
 * Conserva la talla seleccionada cuando está disponible.
 */

(() => {
  'use strict';

  if (window.__linkedPdpKitSwapInstalled) return;
  window.__linkedPdpKitSwapInstalled = true;

  const SWATCH_SELECTOR = 'kit-variant-picker a.kit-swatch[href]';
  const SWAP_EVENT = 'pdp:swapped';

  const normalize = value =>
    String(value ?? '').trim().toLowerCase();

  const normalizePath = path =>
    path.replace(/\/+$/, '');

  let activeController = null;
  let requestNumber = 0;

  // ---------------------------------------
  // SIZE: Read selected size
  // ---------------------------------------

  function sizeInputs(picker) {
    return [...picker.querySelectorAll('[data-option-name]')]
      .filter(group =>
        normalize(group.dataset.optionName) === 'size'
      )
      .map(group =>
        group.querySelector('input[type="radio"]')
      )
      .filter(Boolean);
  }

  function selectedSize(picker) {
    return sizeInputs(picker)
      .find(input => input.checked)?.value || null;
  }

  function targetSizeInput(picker, wantedSize) {
    return sizeInputs(picker).find(input =>
      normalize(input.value) === normalize(wantedSize)
    );
  }

  // ---------------------------------------
  // SECTION HELPERS
  // ---------------------------------------

  function productSectionId(sectionWrapper) {
    return sectionWrapper.querySelector(
      '[data-section-type="product"][data-section-id]'
    )?.dataset.sectionId || null;
  }

  // ---------------------------------------
  // FIND MATCHING VARIANT
  // Shopify Product Ajax API
  // ---------------------------------------

  async function variantIdForSize(targetUrl, size, signal) {
    if (!size) return null;

    const jsonUrl = new URL(targetUrl);

    jsonUrl.pathname =
      jsonUrl.pathname.replace(/\/+$/, '') + '.js';

    jsonUrl.search = '';
    jsonUrl.hash = '';

    const response = await fetch(jsonUrl.href, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      },
      signal
    });

    if (!response.ok) {
      throw new Error(
        `Product API: HTTP ${response.status}`
      );
    }

    const product = await response.json();

    const options = Array.isArray(product.options)
      ? product.options
      : [];

    const optionIndex = options.findIndex(option =>
      normalize(
        typeof option === 'string'
          ? option
          : option?.name
      ) === 'size'
    );

    if (
      optionIndex === -1 ||
      !Array.isArray(product.variants)
    ) {
      return null;
    }

    const option = options[optionIndex];

    const position =
      typeof option === 'object' && option?.position
        ? Number(option.position) - 1
        : optionIndex;

    const variant = product.variants.find(item => {
      const value =
        item.options?.[position] ??
        item[`option${position + 1}`];

      return normalize(value) === normalize(size);
    });

    return variant?.id || null;
  }

  // ---------------------------------------
  // MAIN PRODUCT SWAP
  // ---------------------------------------

  async function swapToLinkedProduct(targetUrl, sourcePicker) {
    const sectionId = sourcePicker.dataset.sectionId;

    const oldWrapper = document.getElementById(
      `shopify-section-${sectionId}`
    );

    // Capture selected size before replacing the PDP.
    const size = selectedSize(sourcePicker);

    if (
      !sectionId ||
      !oldWrapper ||
      !oldWrapper.contains(sourcePicker)
    ) {
      window.location.assign(targetUrl.href);
      return;
    }

    // Cancel previous request when another color is selected.
    activeController?.abort();

    const controller = new AbortController();
    const currentRequest = ++requestNumber;

    activeController = controller;

    oldWrapper.setAttribute('aria-busy', 'true');

    try {
      let variantId = null;

      // ---------------------------------------
      // 1. RESOLVE TARGET SIZE
      // ---------------------------------------

      try {
        variantId = await variantIdForSize(
          targetUrl,
          size,
          controller.signal
        );
      } catch (error) {
        if (error.name === 'AbortError') {
          throw error;
        }

        console.warn(
          '[Linked PDP] Could not resolve size:',
          error
        );
      }

      // ---------------------------------------
      // 2. FETCH TARGET PRODUCT SECTION
      // ---------------------------------------

      const sectionUrl = new URL(targetUrl);

      sectionUrl.hash = '';

      sectionUrl.searchParams.set(
        'section_id',
        sectionId
      );

      if (variantId) {
        sectionUrl.searchParams.set(
          'variant',
          String(variantId)
        );
      }

      const response = await fetch(sectionUrl.href, {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(
          `Section Rendering API: HTTP ${response.status}`
        );
      }

      const html = await response.text();

      if (
        controller.signal.aborted ||
        currentRequest !== requestNumber
      ) {
        return;
      }

      // ---------------------------------------
      // 3. PARSE NEW PRODUCT HTML
      // ---------------------------------------

      const doc = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const nextWrapper = doc.getElementById(
        `shopify-section-${sectionId}`
      );

      const nextPicker = nextWrapper?.querySelector(
        'kit-variant-picker'
      );

      const nextSectionId =
        nextWrapper && productSectionId(nextWrapper);

      if (
        !nextWrapper ||
        !nextPicker ||
        !nextSectionId
      ) {
        throw new Error(
          'Target product section is invalid.'
        );
      }

      // Verify that Shopify returned the requested product.
      const renderedPath = normalizePath(
        new URL(
          nextPicker.dataset.productUrl || targetUrl.href,
          location.origin
        ).pathname
      );

      if (
        renderedPath !== normalizePath(targetUrl.pathname)
      ) {
        throw new Error(
          'Received HTML belongs to another product.'
        );
      }

      const oldSectionId =
        productSectionId(oldWrapper) || sectionId;

      const oldProductTitle =
        sourcePicker.dataset.productTitle;

      // ---------------------------------------
      // 4. UNLOAD OLD IMPULSE PRODUCT INSTANCE
      // ---------------------------------------

      oldWrapper.dispatchEvent(
        new CustomEvent('shopify:section:unload', {
          bubbles: true,
          detail: {
            sectionId: oldSectionId
          }
        })
      );

      // ---------------------------------------
      // 5. REPLACE PRODUCT SECTION
      // ---------------------------------------

      oldWrapper.replaceWith(nextWrapper);

      // ---------------------------------------
      // 6. UPDATE BROWSER URL
      // ---------------------------------------

      const displayUrl = new URL(targetUrl);

      displayUrl.searchParams.delete('section_id');
      displayUrl.searchParams.delete('sections');

      if (variantId) {
        displayUrl.searchParams.set(
          'variant',
          String(variantId)
        );
      }

      history.replaceState(
        history.state,
        '',
        displayUrl.pathname +
          displayUrl.search +
          displayUrl.hash
      );

      // ---------------------------------------
      // 7. REINITIALIZE IMPULSE PRODUCT
      // ---------------------------------------

      nextWrapper.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: {
            sectionId: nextSectionId
          }
        })
      );

      // Verify the product instance was initialized.
      const instances =
        window.theme?.sections?.instances;

      if (
        Array.isArray(instances) &&
        !instances.some(instance =>
          instance.id === nextSectionId &&
          instance.type === 'product' &&
          nextWrapper.contains(instance.container)
        )
      ) {
        throw new Error(
          'Impulse could not initialize theme.Product.'
        );
      }

      // ---------------------------------------
      // 8. SIZE FALLBACK
      // ---------------------------------------

      // If the selected size was not applied by Shopify,
      // trigger the native Kit radio input.
      const matchingInput =
        size && targetSizeInput(nextPicker, size);

      if (
        matchingInput &&
        !matchingInput.checked &&
        !matchingInput.disabled
      ) {
        matchingInput.click();
      }

      // ---------------------------------------
      // 9. PAYMENT BUTTONS
      // ---------------------------------------

      try {
        window.Shopify?.PaymentButton?.init?.();
      } catch (error) {
        console.warn(
          '[Linked PDP] PaymentButton:',
          error
        );
      }

      // ---------------------------------------
      // 10. UPDATE DOCUMENT TITLE
      // ---------------------------------------

      const nextProductTitle =
        nextPicker.dataset.productTitle;

      if (
        oldProductTitle &&
        nextProductTitle &&
        document.title.includes(oldProductTitle)
      ) {
        document.title = document.title.replace(
          oldProductTitle,
          nextProductTitle
        );
      }

      // ---------------------------------------
      // 11. DISPATCH CUSTOM SWAP EVENT
      // ---------------------------------------

      document.dispatchEvent(
        new CustomEvent(SWAP_EVENT, {
          detail: {
            url:
              displayUrl.pathname +
              displayUrl.search,

            sectionId: nextSectionId,

            productHandle:
              nextPicker.dataset.productHandle,

            size,
            variantId
          }
        })
      );

    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(
        '[Linked PDP] Product swap failed:',
        error
      );

      // Fallback to normal navigation.
      window.location.assign(targetUrl.href);

    } finally {
      if (activeController === controller) {
        activeController = null;
        oldWrapper.removeAttribute('aria-busy');
      }
    }
  }

  // ---------------------------------------
  // SWATCH CLICK LISTENER
  // ---------------------------------------

  // Event delegation allows the listener to work
  // even after replacing the entire product section.

  document.addEventListener(
    'click',
    event => {
      // Respect modified clicks and middle-click.
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const swatch = event.target.closest(
        SWATCH_SELECTOR
      );

      if (!swatch) return;

      const picker = swatch.closest(
        'kit-variant-picker'
      );

      if (
        !picker ||
        picker.closest('[data-modal="true"]')
      ) {
        return;
      }

      const targetUrl = new URL(
        swatch.getAttribute('href'),
        location.href
      );

      // Only allow same-origin product links.
      if (targetUrl.origin !== location.origin) {
        return;
      }

      if (
        !/(?:^|\/)products\/[^/]+\/?$/.test(
          targetUrl.pathname
        )
      ) {
        return;
      }

      const currentPath = normalizePath(
        new URL(
          picker.dataset.productUrl ||
            location.pathname,
          location.origin
        ).pathname
      );

      // Do nothing if clicking the current color.
      if (
        normalizePath(targetUrl.pathname) ===
        currentPath
      ) {
        event.preventDefault();
        return;
      }

      // Prevent traditional page navigation.
      event.preventDefault();

      // Execute AJAX product swap.
      swapToLinkedProduct(targetUrl, picker);
    },
    true
  );

})();
